import { jsPDF } from "jspdf";
import { DocumentExportMetadata } from "../../../../shared/document-export";
import { addPdfDocumentFooters } from "../../../../shared/document-export/pdf/PdfDocumentFooter";
import { drawPdfDocumentHeader } from "../../../../shared/document-export/pdf/PdfDocumentHeader";
import { drawPdfKeyValueList } from "../../../../shared/document-export/pdf/PdfKeyValueList";
import { drawPdfSectionHeading } from "../../../../shared/document-export/pdf/PdfSection";
import { drawPdfTable } from "../../../../shared/document-export/pdf/PdfTable";
import { ServiceInstanceDto, ServiceInstanceRotaDto } from "../../../types/types";

export interface RotaExportData {
  instance: ServiceInstanceDto;
  assignments: ServiceInstanceRotaDto[];
}

export interface GenerateRotaPdfOptions {
  organisationName?: string;
  serviceName: string;
  location?: string;
  generatedBy?: string;
  rotas: RotaExportData[];
}

const displayDate = (value: string) =>
  new Intl.DateTimeFormat(undefined, { day: "numeric", month: "long", year: "numeric" }).format(new Date(value));

const displayDayHeading = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(value));

const displayTime = (value: string) =>
  new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" }).format(new Date(value));

const calendarDayKey = (value: string) => {
  const date = new Date(value);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

export const generateRotaPdf = async ({
  organisationName,
  serviceName,
  location,
  generatedBy,
  rotas,
}: GenerateRotaPdfOptions): Promise<Blob> => {
  const activeRotas = rotas
    .map((rota) => ({
      ...rota,
      assignments: rota.assignments.filter((item) => item.status !== "Cancelled" && item.status !== "Removed"),
    }))
    .filter((rota) => rota.assignments.length > 0)
    .sort((left, right) => new Date(left.instance.startDateTime).getTime() - new Date(right.instance.startDateTime).getTime());

  if (activeRotas.length === 0) throw new Error("NO_ROTA_ASSIGNMENTS");

  const firstDate = activeRotas[0].instance.startDateTime;
  const lastDate = activeRotas[activeRotas.length - 1].instance.endDateTime;
  const title = `${serviceName} Rota`;
  const metadata: DocumentExportMetadata = {
    documentType: "rota-summary",
    title,
    subtitle: `${displayDate(firstDate)}${displayDate(firstDate) === displayDate(lastDate) ? "" : ` - ${displayDate(lastDate)}`}`,
    organisationName,
    serviceName,
    createdDate: new Date().toLocaleString(),
    createdBy: generatedBy,
  };
  const document = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  document.setProperties({ title, subject: "Volunteer rota summary", creator: "FrontLine" });

  let y = drawPdfDocumentHeader(document, metadata);
  y = drawPdfKeyValueList(
    document,
    [
      { label: "Date range", value: metadata.subtitle ?? "" },
      ...(location ? [{ label: "Location", value: location }] : []),
      ...(generatedBy ? [{ label: "Generated by", value: generatedBy }] : []),
    ],
    y,
  );
  const rotasByDay = activeRotas.reduce<RotaExportData[][]>((days, rota) => {
    const previousDay = days[days.length - 1];
    const previousDate = previousDay?.[0].instance.startDateTime;

    if (!previousDate || calendarDayKey(previousDate) !== calendarDayKey(rota.instance.startDateTime)) {
      days.push([rota]);
    } else {
      previousDay.push(rota);
    }

    return days;
  }, []);

  rotasByDay.forEach((dayRotas, dayIndex) => {
    if (dayIndex > 0) {
      document.addPage();
      y = drawPdfDocumentHeader(document, metadata, true);
    }

    y = drawPdfSectionHeading(
      document,
      displayDayHeading(dayRotas[0].instance.startDateTime),
      y + 2,
    );

    const rows = dayRotas.flatMap(({ instance, assignments }) =>
      assignments.map((assignment) => [
        `${displayTime(instance.startDateTime)}-${displayTime(instance.endDateTime)}`,
        assignment.partyName,
        assignment.serviceRoleName,
        assignment.status,
      ]),
    );

    y = drawPdfTable(document, metadata, ["Time", "Person", "Role", "Status"], rows, y);
  });

  addPdfDocumentFooters(document, metadata);
  return document.output("blob");
};
